import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  Linking,
  Platform,
  ScrollView,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer } from '@legendapp/state/react';
import { useRouter, useFocusEffect } from 'expo-router';
import { globalState, UserProfile, uiActions } from '../../state/globalState';
import { AppAppIndexLogger } from '../../utils/logger';
import theme from '../../config/theme';
import { logout } from '../../services/authService';
import { getUserProfile, getUserCompany } from '../../services/userService';
import { databaseService } from '../../services/databaseService';
import { syncAll } from '../../services/syncService';
import { useHomeSyncStatus } from '@/hooks/useHomeSyncStatus';
import { ChecklistsList } from '../../components/checklist/ChecklistsList';
import { debugPause } from '@/utils/debugPauseUniversal';
import { Ionicons } from '@expo/vector-icons';
import { APP_CONSTANTS } from '../../config/constants';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';
import { realtimeTrigger } from '../../state/realtime';
import { exportImageTracesCSV, clearImageTraces } from '@/utils/debugImageTrace';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import TrialBanner from '@/components/subscription/TrialBanner';
import { hasPremiumAccess } from '@/utils/subscriptionAccess';
import { isMotoristaUserType } from '@/utils/userRoles';
import {
  canAssignChecklist,
  getAssignableChecklistDrivers,
  getChecklistCreationUserId,
  type ChecklistAssignmentCandidate,
  type ChecklistCreationMode,
} from '@/utils/checklistAssignment';
import { isIOS } from '@/utils/platform';
import { FirstChecklistOnboarding } from '@/components/onboarding/FirstChecklistOnboarding';
import CompanyBrandingOnboarding from '@/components/onboarding/CompanyBrandingOnboarding';
import ProfileBrandingOnboarding from '@/components/onboarding/ProfileBrandingOnboarding';
import ChecklistAssignmentOnboarding from '@/components/onboarding/ChecklistAssignmentOnboarding';
import {
  buildChecklistAssignmentOnboardingKey,
  persistChecklistAssignmentOnboardingDismissed,
  readChecklistAssignmentOnboardingDismissed,
} from '@/utils/checklistAssignmentOnboardingStorage';
import {
  checklistSnapshotCreationService,
  getChecklistSnapshotCreationClientBuild,
} from '@/services/checklist/ChecklistSnapshotCreationRuntime';
// Logger específico para esta tela
const logger = AppAppIndexLogger.Main;

async function logPendings() {
  const [ops, imgs, dels] = await Promise.all([
    databaseService.listItems('pending_operations'),
    databaseService.listItems('pending_images'),
    databaseService.listItems('pending_deletions')
  ]);
  logger.info(`>>> Estado das pendências → ops: ${ops.length}, imgs: ${imgs.length}, dels: ${dels.length}`);
}

const HomeScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const user = globalState.user.user.get();
  const existsInDatabase = globalState.user.existsInDatabase.get();
  const isConnected = globalState.network.isConnected.get();
  const appReady = globalState.app.isReady.get();
  const directSignupState = globalState.user.directSignup.get();

  // Estados locais
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [syncingPending, setSyncingPending] = useState(false);
  const [showTemplateSheet, setShowTemplateSheet] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [checklistCreationMode, setChecklistCreationMode] = useState<ChecklistCreationMode | null>(null);
  const [showDriverSheet, setShowDriverSheet] = useState(false);
  const [drivers, setDrivers] = useState<ChecklistAssignmentCandidate[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [creatingChecklist, setCreatingChecklist] = useState(false);
  const [showAssignmentOnboarding, setShowAssignmentOnboarding] = useState(false);
  const [assignmentOnboardingDismissed, setAssignmentOnboardingDismissed] = useState(false);
  const creatingChecklistRef = useRef(false);
  const templateLoadRequestRef = useRef(0);
  const driverLoadRequestRef = useRef(0);

  const syncState = globalState.sync;
  const globalSyncRunning = syncState.isRunning.get();
  const syncProgress = syncState.progress.get();
  const normalizedStageProgress = Number.isFinite(syncProgress.stageProgress)
    ? Math.min(100, Math.max(0, Math.round(syncProgress.stageProgress)))
    : 0;

  // A cópia da tela travada do bootstrap precisa ser mais humana do que o
  // estado técnico do sync, mas sem esconder o progresso real do primeiro sync.
  const loadingStageCopy: Record<
    'idle' | 'prerequisites' | 'push' | 'pull' | 'cleanup',
    { step: number; title: string; hint: string }
  > = {
    idle: {
      step: 0,
      title: 'Preparando seu aplicativo',
      hint: 'Estamos organizando seu acesso para começar com segurança.',
    },
    prerequisites: {
      step: 1,
      title: 'Preparando seu aplicativo',
      hint: 'Estamos conectando sua conta e preparando este aparelho para trabalhar com os checklists.',
    },
    push: {
      step: 2,
      title: 'Conferindo informações salvas',
      hint: 'Se houver envios pendentes, eles continuarão após a preparação do aplicativo.',
    },
    pull: {
      step: 3,
      title: normalizedStageProgress >= 90
        ? 'Quase tudo pronto'
        : normalizedStageProgress >= 70
          ? 'Conferindo seus modelos'
          : normalizedStageProgress >= 60
            ? 'Baixando as imagens dos modelos'
            : normalizedStageProgress >= 45
              ? 'Organizando os campos dos modelos'
              : 'Baixando os dados da sua empresa',
      hint: normalizedStageProgress >= 90
        ? 'Estamos finalizando a preparação para liberar seu acesso.'
        : normalizedStageProgress >= 70
          ? 'Os modelos da sua empresa estão quase prontos para uso.'
          : normalizedStageProgress >= 60
            ? 'Estamos salvando as imagens dos veículos para os checklists funcionarem também sem internet.'
            : normalizedStageProgress >= 45
              ? 'Estamos deixando as perguntas e opções dos modelos prontas para você.'
              : 'No primeiro acesso, salvamos os dados da empresa neste aparelho para permitir o uso offline.',
    },
    cleanup: {
      step: 4,
      title: 'Finalizando a preparação',
      hint: 'Falta pouco. Estamos conferindo se tudo ficou pronto para você começar a usar.',
    },
  };

  const currentLoadingStage = loadingStageCopy[syncProgress.stage] ?? loadingStageCopy.idle;
  const progressRanges = {
    prerequisites: { start: 38, end: 42 },
    push: { start: 42, end: 48 },
    pull: { start: 48, end: 95 },
    cleanup: { start: 95, end: 100 },
  } as const;
  const currentProgressRange = syncProgress.stage === 'idle'
    ? null
    : progressRanges[syncProgress.stage];
  const calculatedInitialLoadingProgress = globalSyncRunning && currentProgressRange
    ? Math.min(96, Math.round(
      currentProgressRange.start +
      ((currentProgressRange.end - currentProgressRange.start) * normalizedStageProgress / 100)
    ))
    : directSignupState.lastCompletedAt
      ? 38
      : 0;
  const [displayedInitialLoadingProgress, setDisplayedInitialLoadingProgress] = useState(
    calculatedInitialLoadingProgress
  );

  useEffect(() => {
    if (appReady) return;

    setDisplayedInitialLoadingProgress(current => (
      Math.max(current, calculatedInitialLoadingProgress)
    ));

  }, [appReady, calculatedInitialLoadingProgress]);

  const initialLoadingMeta = displayedInitialLoadingProgress > 0
    ? `${displayedInitialLoadingProgress}% concluído`
    : 'Preparando...';
  const displayedLoadingStage = displayedInitialLoadingProgress >= 90
    ? {
      title: 'Quase tudo pronto',
      hint: 'Estamos fazendo a última conferência antes de liberar seu acesso.',
    }
    : currentLoadingStage;

  const syncProfile = globalState.user.profile.get();
  const { pendingMessage, noticeVisible, dismissNotice, toggleNotice, refresh: refreshHomePending } = useHomeSyncStatus({
    userId: user?.id,
    companyId: syncProfile?.id === user?.id ? syncProfile?.empresa_id : null,
    ready: appReady,
    online: isConnected,
    busy: globalSyncRunning || syncingPending,
    lastSyncDate: syncState.lastSyncDate.get(),
  });
  useFocusEffect(refreshHomePending);
  const syncBusy = globalSyncRunning || syncingPending;
  const statusBadgeText = !isConnected
    ? 'Offline'
    : syncBusy
      ? (syncingPending || syncProgress.stage === 'push' ? 'Enviando\ndados' : 'Atualizando\ndados')
      : pendingMessage ? 'Envio\npendente' : 'Online';
  const statusBadgeColor = !isConnected
    ? theme.colors.util.warning
    : syncBusy || pendingMessage ? theme.colors.primary.default : theme.colors.util.success;
  const canToggleNotice = Boolean(pendingMessage) && !syncBusy;

  const shouldShowDebugActions = APP_CONSTANTS.FEATURE_FLAGS?.SHOW_DEBUG_ACTIONS !== false;
  const subscriptionSnapshot = globalState.subscription.get();
  const globalProfile = globalState.user.profile.get() as UserProfile | null;
  const effectiveProfile: UserProfile | null = globalProfile ?? (userProfile as UserProfile | null);
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, effectiveProfile);
  const onboardingDismissed = globalState.ui.onboarding.firstChecklistDismissed.get();
  const companyTipDismissed = globalState.ui.onboarding.companyTipDismissed.get();
  const profileTipDismissed = globalState.ui.onboarding.profileTipDismissed.get();
  const [hasChecklistItems, setHasChecklistItems] = useState<boolean | null>(null);

  const directDefaults = APP_CONSTANTS.REGISTRATION.DIRECT_SIGNUP_DEFAULTS ?? {};
  const defaultCompanyName = (directDefaults.COMPANY_NAME || 'Nome da sua Empresa').trim().toLowerCase();
  const defaultUserName = (directDefaults.USER_NAME || 'Seu nome').trim().toLowerCase();
  const normalizedCompanyName = (company?.nome || '').trim().toLowerCase();
  const normalizedUserName = (effectiveProfile?.name || userProfile?.name || '').trim().toLowerCase();
  const companyNameIsDefault = normalizedCompanyName === defaultCompanyName;
  const usingDefaultCompanyData = companyNameIsDefault || normalizedUserName === defaultUserName;
  const newAccountPlaceholders = usingDefaultCompanyData || normalizedUserName === defaultUserName;

  const trialPrefix = APP_CONSTANTS.SUBSCRIPTION_TRIAL?.EXTERNAL_ID_PREFIX || 'manual:trial-';
  const subscriptionExternalId = subscriptionSnapshot.externalId || '';
  const statusIsActive = subscriptionSnapshot.status === 'ativa' || subscriptionSnapshot.status === 'cancelamento_agendado';
  const isTrialSubscription =
    statusIsActive &&
    !!subscriptionExternalId &&
    subscriptionExternalId.startsWith(trialPrefix) &&
    subscriptionSnapshot.billingProvider === 'manual';
  const trialDismissedId = globalState.ui.trialBanner.dismissedForExternalId.get();
  const trialBannerUnlocked = onboardingDismissed || !!hasChecklistItems;
  const showTrialBanner =
    !isIOS && trialBannerUnlocked && isTrialSubscription && trialDismissedId !== subscriptionExternalId;

  const handleDismissTrialBanner = () => {
    uiActions.dismissTrialBanner(subscriptionExternalId);
  };

  const handleTrialUpgrade = () => {
    router.push('/settings/subscription/plans');
  };

  const canAccessChecklists = premiumAccess && !!userProfile?.empresa_id;
  const safeTopInset = Platform.OS === 'web' ? 0 : safeAreaInsets.top;
  const headerTopPadding = Math.max(theme.spacing.md, safeTopInset + theme.spacing.xs);
  const shouldShowFirstChecklistOnboarding =
    !!userProfile?.empresa_id &&
    !onboardingDismissed &&
    hasChecklistItems === false &&
    (isTrialSubscription || newAccountPlaceholders);
  const userIsMotorista = isMotoristaUserType(effectiveProfile?.user_type);
  const canAssignChecklistFromHome = canAccessChecklists && canAssignChecklist(effectiveProfile?.user_type);
  const checklistScopeUserId = userIsMotorista ? user?.id || null : null;
  const shouldShowCompanyBrandingTip =
    !shouldShowFirstChecklistOnboarding &&
    hasChecklistItems === true &&
    !companyTipDismissed &&
    companyNameIsDefault &&
    !userIsMotorista;
  const shouldShowProfileBrandingTip =
    !shouldShowFirstChecklistOnboarding &&
    hasChecklistItems === true &&
    companyTipDismissed &&
    !profileTipDismissed &&
    (globalProfile?.name?.trim().toLowerCase() ===
      (APP_CONSTANTS.REGISTRATION.DIRECT_SIGNUP_DEFAULTS?.USER_NAME || 'Seu nome').trim().toLowerCase());
  const onboardingCompanyName = company?.nome ?? userProfile?.empresa_id ?? '';

  useEffect(() => {
    logger.info(
      `[ONBOARDING] Avaliando card inicial | trial=${isTrialSubscription} | placeholders=${newAccountPlaceholders} | empresa=${!!userProfile?.empresa_id} | dismissed=${onboardingDismissed} | hasItems=${hasChecklistItems} | show=${shouldShowFirstChecklistOnboarding}`
    );
  }, [
    isTrialSubscription,
    newAccountPlaceholders,
    userProfile?.empresa_id,
    onboardingDismissed,
    hasChecklistItems,
    shouldShowFirstChecklistOnboarding,
  ]);

  //logger.debug('Renderizando HomeScreen com usuário:', user?.id);

  // Verificar e sincronizar operações pendentes quando a tela recebe foco
  useFocusEffect(
    React.useCallback(() => {
      if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
        logger.warn('[RECOVERY] Processamento de pendencias da Home bloqueado pela quarentena');
        return () => undefined;
      }

      // Log ao focar na tela
      //logger.info('==== FOCO NA TELA HOME ====');
      
      // Incrementa contador realtimeTrigger para forçar refresh da lista ao voltar para Home
      realtimeTrigger.counter.set((c) => {
        const newVal = c + 1;
        //logger.info(`[REALTIME_TRIGGER] Counter incrementado na Home para ${newVal}`);
        return newVal;
      });
      
      const checkAndSyncPendingOperations = async () => {
        //await debugPause('app app index useFocusEffect checkAndSyncPendingOperations');
        try {
          // Verificar se está online e se a aplicação está pronta
          if (!isConnected || !appReady || !user?.id) {
            logger.debug('Não é possível sincronizar: offline, app não pronto ou usuário não autenticado');
            return;
          }

          if (globalState.sync.isRunning.get()) {
            logger.info('Sincronização global ativa detectada. Fluxo local aguardará o término.');
            return;
          }

          // Verificar se há operações pendentes
          //logger.info('Verificando operações pendentes');
          const pendingOps = await databaseService.getPendingOperations();
          logger.info(`Operações pendentes encontradas: ${pendingOps.length}`);

          // Verificar se há imagens pendentes
          //logger.info('Verificando imagens pendentes');
          const pendingImages = await databaseService.listItems('pending_images');
          logger.info(`Imagens pendentes encontradas: ${pendingImages.length}`);

          // Verificar se há deleções pendentes
          //logger.info('Verificando deleções pendentes');
          const pendingDeletions = await databaseService.listItems('pending_deletions');
          logger.info(`Deleções pendentes encontradas: ${pendingDeletions.length}`);

          //logger.info('>>> Mostrando logpendings antes de verificar operações pendentes');
          await logPendings();
          await debugPause('fez Log Pendings antes de verificar operações pendentes');
          if (pendingOps.length > 0 || pendingImages.length > 0 || pendingDeletions.length > 0) {
            logger.info(`Detectadas ${pendingOps.length} operações, ${pendingImages.length} imagens e ${pendingDeletions.length} deleções pendentes ao focar na tela inicial`);

            setSyncingPending(true);
            await logPendings();
            await debugPause('antes de syncAll ao focar tela inicial');
            const result = await syncAll();
            await logPendings();
            await debugPause('após syncAll ao focar tela inicial');

            if (result.success) {
              logger.info('Sincronização de pendências ao focar a tela inicial concluída com sucesso');
              const userId = globalState.user.user.get()?.id;
              if (userId) {
                await getUserProfile(userId);
              }
            } else {
              logger.warn(`Sincronização de pendências ao focar a tela inicial não concluiu: ${result.message}`);
            }
          } else {
            logger.debug('Nenhuma operação ou imagem pendente para sincronizar');
          }
        } catch (error) {
          logger.error('Erro ao verificar/sincronizar operações pendentes:', error);
        } finally {
          setSyncingPending(false);
        }
      };


      checkAndSyncPendingOperations();

      return () => {
        // Cleanup se necessário
      };
    }, [appReady, user?.id])
  );

  // Efeito para carregar dados do usuário
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Verificar se a aplicação está pronta antes de carregar dados
        if (!appReady) {
          AppAppIndexLogger.Init.debug('Aguardando aplicação ficar pronta para carregar dados do usuário');
          return;
        }

        AppAppIndexLogger.Init.debug('Iniciando carregamento de dados do usuário');
        setLoading(true);

        if (!user?.id) {
          AppAppIndexLogger.Init.warn('ID do usuário não disponível');
          setLoading(false);
          return;
        }

        // Obter dados do perfil do usuário
        const { data: profile, error: profileError } = await getUserProfile(user.id);

        if (profileError) {
          AppAppIndexLogger.Init.error('Erro ao obter perfil do usuário:', profileError);
          Alert.alert('Erro', 'Não foi possível carregar seu perfil.');
          setLoading(false);
          return;
        }

        setUserProfile(profile);

        // Se o usuário pertence a uma empresa, obter dados da empresa
        if (profile?.empresa_id) {
          const { data: companyData, error: companyError } = await getUserCompany(profile.empresa_id);

          if (companyError) {
            AppAppIndexLogger.Init.error('Erro ao obter dados da empresa:', companyError);
          } else {
            setCompany(companyData);
            AppAppIndexLogger.Init.info('Dados da empresa carregados com sucesso');
          }
        } else {
          AppAppIndexLogger.Init.info('Usuário não possui empresa associada');
        }

        setLoading(false);
      } catch (error) {
        AppAppIndexLogger.Init.error('Erro ao carregar dados do usuário:', error);
        setLoading(false);
        Alert.alert('Erro', 'Ocorreu um erro ao carregar seus dados.');
      }
    };

    loadUserData();
  }, [user?.id, appReady]);

  useEffect(() => {
    if (!appReady || !userProfile?.empresa_id) {
      return;
    }
    if (hasChecklistItems !== null) {
      return;
    }

    let isMounted = true;
    const checkExistingChecklists = async () => {
      try {
        const checklistFilter: Record<string, any> = {
          empresa_id: userProfile.empresa_id,
          limit: 1,
        };

        if (userIsMotorista && user?.id) {
          checklistFilter.usuario_id = user.id;
        }

        const existing = await databaseService.listItems(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
          checklistFilter
        );
        if (isMounted) {
          setHasChecklistItems(existing.length > 0);
          logger.info(
            `[ONBOARDING] Verificação inicial de checklists → encontrados=${existing.length} | escopo=${userIsMotorista ? 'motorista' : 'empresa'}`
          );
        }
      } catch (error) {
        logger.warn('Não foi possível verificar existência de checklists ao iniciar onboarding', error);
      }
    };

    checkExistingChecklists();

    return () => {
      isMounted = false;
    };
  }, [appReady, userProfile?.empresa_id, hasChecklistItems, userIsMotorista, user?.id]);

  const handleLogout = async () => {
    try {
      logger.debug('Iniciando processo de logout');

      const { success, error } = await logout();

      if (error) {
        logger.error('Erro ao fazer logout:', error);
        Alert.alert('Erro', 'Não foi possível fazer logout');
        return;
      }

      if (success) {
        logger.info('Logout realizado com sucesso');
        // A navegação será gerenciada pelo router principal com base no estado
      }
    } catch (error) {
      logger.error('Exceção durante logout:', error);
      Alert.alert('Erro', 'Ocorreu um erro durante o logout');
    }
  };

  const resetChecklistCreationFlow = () => {
    templateLoadRequestRef.current += 1;
    driverLoadRequestRef.current += 1;
    setShowTemplateSheet(false);
    setShowDriverSheet(false);
    setLoadingDrivers(false);
    setChecklistCreationMode(null);
    setSelectedTemplate(null);
    setDrivers([]);
  };

  const openTemplateSheet = async (mode: ChecklistCreationMode) => {
    if (!userProfile?.empresa_id || (mode === 'assign' && !canAssignChecklistFromHome)) return;
    const requestId = templateLoadRequestRef.current + 1;
    templateLoadRequestRef.current = requestId;
    setChecklistCreationMode(mode);
    setLoadingTemplates(true);
    setShowTemplateSheet(true);
    try {
      const result = await databaseService.listItems(
        APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
        { empresa_id: userProfile.empresa_id }
      );
      if (requestId !== templateLoadRequestRef.current) return;
      setTemplates(result.filter((template: any) => !template.retired_at));
    } catch (err) {
      if (requestId !== templateLoadRequestRef.current) return;
      logger.warn('Não foi possível carregar os modelos para criar checklist', err);
      setTemplates([]);
    } finally {
      if (requestId === templateLoadRequestRef.current) {
        setLoadingTemplates(false);
      }
    }
  };

  // Função para abrir o Bottom Sheet e buscar templates do fluxo normal.
  const handleOpenFab = async () => openTemplateSheet('self');

  const assignmentOnboardingKey = buildChecklistAssignmentOnboardingKey(
    user?.id,
    userProfile?.empresa_id
  );

  useEffect(() => {
    setAssignmentOnboardingDismissed(
      readChecklistAssignmentOnboardingDismissed(assignmentOnboardingKey)
    );
  }, [assignmentOnboardingKey]);

  const dismissAssignmentOnboarding = useCallback(() => {
    persistChecklistAssignmentOnboardingDismissed(assignmentOnboardingKey);
    setAssignmentOnboardingDismissed(true);
    setShowAssignmentOnboarding(false);
  }, [assignmentOnboardingKey]);

  const handleOpenAssignFab = async () => {
    if (!assignmentOnboardingDismissed) {
      setShowAssignmentOnboarding(true);
      return;
    }
    await openTemplateSheet('assign');
  };

  const handleContinueAssignmentOnboarding = useCallback(() => {
    dismissAssignmentOnboarding();
    void openTemplateSheet('assign');
  }, [dismissAssignmentOnboarding]);

  const handleAssignmentWhatsApp = useCallback(() => {
    void Linking.openURL(APP_CONSTANTS.CONTACTS.WHATSAPP_URL).catch((error) => {
      logger.error('Falha ao abrir WhatsApp pelo onboarding de atribuição:', error);
      Alert.alert('WhatsApp', 'Não foi possível abrir o WhatsApp neste dispositivo.');
    });
  }, []);

  const openDriverSheet = async (template: any) => {
    if (!userProfile?.empresa_id) {
      resetChecklistCreationFlow();
      Alert.alert('Erro', 'Não foi possível identificar a empresa para atribuir o checklist.');
      return;
    }

    const requestId = driverLoadRequestRef.current + 1;
    driverLoadRequestRef.current = requestId;
    setSelectedTemplate(template);
    setLoadingDrivers(true);
    setShowDriverSheet(true);
    try {
      const result = await databaseService.listItems<ChecklistAssignmentCandidate>(
        APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
        { empresa_id: userProfile.empresa_id }
      );
      if (requestId !== driverLoadRequestRef.current) return;
      const eligibleDrivers = getAssignableChecklistDrivers(result, userProfile.empresa_id);
      setDrivers(eligibleDrivers);
      if (eligibleDrivers.length === 0) {
        resetChecklistCreationFlow();
        Alert.alert('Nenhum motorista disponível', 'Cadastre um motorista na empresa para atribuir este checklist.');
      }
    } catch (error) {
      if (requestId !== driverLoadRequestRef.current) return;
      logger.error('Falha ao carregar motoristas para atribuição:', error);
      resetChecklistCreationFlow();
      Alert.alert('Não foi possível carregar os motoristas', 'Tente novamente. Nenhum checklist foi criado.');
    } finally {
      if (requestId === driverLoadRequestRef.current) {
        setLoadingDrivers(false);
      }
    }
  };

  const createChecklistForUser = async (template: any, targetUserId?: string | null) => {
    const mode = checklistCreationMode ?? 'self';
    const actorUserId = user?.id;
    const creationUserId = getChecklistCreationUserId({
      mode,
      currentUserId: actorUserId,
      targetUserId,
    });

    if (!userProfile?.empresa_id || !creationUserId || !actorUserId) {
      Alert.alert('Erro', 'Não foi possível identificar o usuário para criar o checklist.');
      return;
    }
    if (creatingChecklistRef.current) return;

    creatingChecklistRef.current = true;
    setCreatingChecklist(true);
    try {
      const result = await checklistSnapshotCreationService.create({
        companyId: userProfile.empresa_id,
        userId: creationUserId,
        actorUserId,
        clientBuild: getChecklistSnapshotCreationClientBuild(),
        template,
      });
      setHasChecklistItems(true);
      if (!onboardingDismissed) {
        uiActions.dismissFirstChecklistOnboarding();
        logger.info('[ONBOARDING] Checklist criado via Home → card será dispensado automaticamente');
      }
      router.push(`/checklist/${result.checklist.id}`);
    } catch (error) {
      logger.error('Falha ao criar checklist local com snapshot:', error);
      Alert.alert('Não foi possível criar o checklist', 'Tente novamente. Nenhum checklist incompleto foi aberto.');
    } finally {
      creatingChecklistRef.current = false;
      setCreatingChecklist(false);
    }
  };

  // No fluxo de atribuição, o motorista só é conhecido após a segunda sheet.
  const handleSelectTemplate = async (template: any) => {
    setShowTemplateSheet(false);
    if (checklistCreationMode === 'assign') {
      await openDriverSheet(template);
      return;
    }

    await createChecklistForUser(template, user?.id);
    resetChecklistCreationFlow();
  };

  const handleSelectDriver = async (driver: ChecklistAssignmentCandidate) => {
    const template = selectedTemplate;
    if (!template || !driver.id || checklistCreationMode !== 'assign') {
      resetChecklistCreationFlow();
      return;
    }

    setShowDriverSheet(false);
    await createChecklistForUser(template, driver.id);
    setChecklistCreationMode(null);
    setSelectedTemplate(null);
    setDrivers([]);
  };

  const handleHasItemsChange = useCallback((hasItems: boolean) => {
    setHasChecklistItems(hasItems);
    if (hasItems && !onboardingDismissed) {
      uiActions.dismissFirstChecklistOnboarding();
      logger.info('[ONBOARDING] Lista detectou checklists existentes → card dispensado');
    }
    logger.info(`[ONBOARDING] onHasItemsChange disparado → hasItems=${hasItems}`);
  }, [onboardingDismissed]);

  const handleOnboardingDismiss = useCallback(() => {
    if (!onboardingDismissed) {
      uiActions.dismissFirstChecklistOnboarding();
      logger.info('[ONBOARDING] Usuário descartou manualmente o card de onboarding');
    }
  }, [onboardingDismissed]);

  const handleUpdateCompanyFromTip = useCallback(() => {
    uiActions.dismissCompanyBrandingTip();
    router.push('/settings/company');
  }, [router]);

  const handleDismissCompanyBrandingTip = useCallback(() => {
    uiActions.dismissCompanyBrandingTip();
  }, []);

  const handleUpdateProfileFromTip = useCallback(() => {
    uiActions.dismissProfileBrandingTip();
    router.push('/settings/account');
  }, [router]);

  const handleDismissProfileBrandingTip = useCallback(() => {
    uiActions.dismissProfileBrandingTip();
  }, []);

  // Renderizar estado de carregamento
  if (!appReady) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingCard}>
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
          <Text style={styles.loadingTitle}>{displayedLoadingStage.title}</Text>
          <View style={styles.loadingProgressTrack}>
            <View
              style={[
                styles.loadingProgressFill,
                { width: `${displayedInitialLoadingProgress}%` },
              ]}
            />
          </View>
          <Text style={styles.loadingProgressMeta}>{initialLoadingMeta}</Text>
          <Text style={styles.loadingText}>{displayedLoadingStage.hint}</Text>
        </View>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
        <Text style={styles.loadingText}>Carregando dados...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header compacto */}
      <View style={[styles.header, { paddingTop: headerTopPadding }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <Text style={styles.welcomeText} numberOfLines={1}>
              Olá, {effectiveProfile?.name || userProfile?.name || user?.email?.split('@')[0] || 'Usuário'}
            </Text>
            <Text style={styles.versionText}>
              Versão do sistema: {APP_CONSTANTS.VERSION}
            </Text>
          </View>

          <View style={styles.headerActions}>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={`Ver estado dos envios. ${statusBadgeText.replace('\n', ' ')}`}
              accessibilityState={{ expanded: noticeVisible, disabled: !canToggleNotice }}
              {...(Platform.OS === 'web' ? { 'aria-expanded': noticeVisible } : {})}
              onPress={toggleNotice}
              disabled={!canToggleNotice}
              style={[styles.statusBadge, {
                backgroundColor: statusBadgeColor
              }]}
            >
              <Text maxFontSizeMultiplier={1.2} style={[styles.statusText, statusBadgeText.includes('\n') && styles.statusTextMultiline]}>
                {statusBadgeText}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.logoutChip}
              onPress={handleLogout}
              accessibilityLabel="Sair da conta"
            >
              <Ionicons
                name="log-out-outline"
                size={18}
                color={theme.colors.text.secondary}
                style={styles.logoutIcon}
              />
              <Text style={styles.logoutChipText}>Sair</Text>
            </TouchableOpacity>
          </View>
        </View>

        {shouldShowDebugActions && premiumAccess && (
          <View style={styles.quickActions}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => router.push('/profile')}
            >
              <Text style={styles.actionButtonText}>Perfil</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionButton, styles.actionButtonSecondary]}
              onPress={() => router.push('/checklists')}
            >
              <Text style={[styles.actionButtonText, styles.actionButtonTextSecondary]}>
                Ver Todos
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionButton, styles.actionButtonSecondary]}
              onPress={() => router.push('./field-ordering')}
            >
              <Text style={[styles.actionButtonText, styles.actionButtonTextSecondary]}>
                Ordenar Campos
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionButton, styles.actionButtonSecondary]}
              onPress={() => router.push('/debug')}
            >
              <Text style={[styles.actionButtonText, styles.actionButtonTextSecondary]}>
                Debug
              </Text>
            </TouchableOpacity>

            {APP_CONSTANTS.DEBUG?.IMAGE_TRACE && (
              <TouchableOpacity
                style={[styles.actionButton, styles.actionButtonSecondary]}
                onPress={() => exportImageTracesCSV()}
              >
                <Text style={[styles.actionButtonText, styles.actionButtonTextSecondary]}>
                  Exportar Logs Imagem (CSV)
                </Text>
              </TouchableOpacity>
            )}

            {APP_CONSTANTS.DEBUG?.IMAGE_TRACE && (
              <TouchableOpacity
                style={[styles.actionButton, styles.actionButtonSecondary]}
                onPress={() => clearImageTraces()}
              >
                <Text style={[styles.actionButtonText, styles.actionButtonTextSecondary]}>
                  Limpar Logs Imagem
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      {noticeVisible && (
        <View style={styles.syncBanner} testID="home-sync-status" accessibilityLiveRegion="polite">
          <Text style={styles.syncBannerMessage}>{pendingMessage}</Text>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Ocultar aviso de envios"
            onPress={dismissNotice}
            style={styles.syncBannerClose}
            hitSlop={6}
          >
            <Ionicons name="close" size={20} color="#28456F" />
          </TouchableOpacity>
        </View>
      )}

      {shouldShowFirstChecklistOnboarding && (
        <View pointerEvents="box-none" style={styles.onboardingOverlay}>
          <View style={styles.onboardingCardWrapper}>
            <FirstChecklistOnboarding
              visible={shouldShowFirstChecklistOnboarding}
              companyName={onboardingCompanyName}
              usingDefaultCompany={usingDefaultCompanyData}
              onDismiss={handleOnboardingDismiss}
            />
          </View>
        </View>
      )}

      {shouldShowCompanyBrandingTip && (
        <View pointerEvents="box-none" style={[styles.onboardingOverlay, styles.onboardingOverlaySecondary]}>
          <View style={styles.onboardingCardWrapper}>
            <CompanyBrandingOnboarding
              visible={shouldShowCompanyBrandingTip}
              companyName={onboardingCompanyName}
              onUpdateCompany={handleUpdateCompanyFromTip}
              onDismiss={handleDismissCompanyBrandingTip}
            />
          </View>
        </View>
      )}

      {shouldShowProfileBrandingTip && (
        <View pointerEvents="box-none" style={[styles.onboardingOverlay, styles.onboardingOverlaySecondary, styles.onboardingOverlayTertiary]}>
          <View style={styles.onboardingCardWrapper}>
            <ProfileBrandingOnboarding
              visible={shouldShowProfileBrandingTip}
              userName={globalProfile?.name}
              onUpdateProfile={handleUpdateProfileFromTip}
              onDismiss={handleDismissProfileBrandingTip}
            />
          </View>
        </View>
      )}

      {showTrialBanner && (
        <TrialBanner
          expiresAt={subscriptionSnapshot.expiration}
          onClose={handleDismissTrialBanner}
          onUpgrade={handleTrialUpgrade}
        />
      )}

      {/* Widget de checklists compacto ou CTA de upgrade */}
      {canAccessChecklists ? (
        <ChecklistsList
          empresaId={userProfile.empresa_id}
          usuarioId={checklistScopeUserId}
          compact={true}
          showFilters={true}
          showHeader={true}
          onHasItemsChange={handleHasItemsChange}
        />
      ) : (
        <UpgradePrompt variant="home" />
      )}

      {/* FABs flutuantes: o fluxo normal fica acima do despacho para preservar a ação existente. */}
      {canAccessChecklists && (
        <View style={styles.fabStack}>
          <View style={styles.fabAction}>
            <TouchableOpacity
              style={styles.fab}
              onPress={handleOpenFab}
              activeOpacity={0.8}
              disabled={creatingChecklist}
              accessibilityLabel="Criar checklist"
              accessibilityHint="Escolha um modelo para criar um checklist para você"
            >
              <Ionicons name="add" size={32} color="#fff" />
            </TouchableOpacity>
            <View style={styles.fabLabelContainer}>
              <Text style={styles.fabLabelText}>{creatingChecklist ? 'Criando checklist… aguarde' : 'Criar checklist'}</Text>
            </View>
          </View>
          {canAssignChecklistFromHome && (
            <View style={styles.fabAction}>
              <TouchableOpacity
                style={[styles.fab, styles.fabAssign]}
                onPress={handleOpenAssignFab}
                activeOpacity={0.8}
                disabled={creatingChecklist}
                accessibilityLabel="Atribuir checklist"
                accessibilityHint="Escolha um modelo e um motorista da sua empresa"
              >
                <Ionicons name="person-add-outline" size={28} color="#fff" />
              </TouchableOpacity>
              <View style={styles.fabLabelContainer}>
                <Text style={styles.fabLabelText}>Atribuir checklist</Text>
              </View>
            </View>
          )}
        </View>
      )}

      {/* Bottom Sheet/Modal de seleção de template */}
      <ChecklistAssignmentOnboarding
        visible={showAssignmentOnboarding}
        onClose={dismissAssignmentOnboarding}
        onContinue={handleContinueAssignmentOnboarding}
        onWhatsApp={handleAssignmentWhatsApp}
      />

      {showTemplateSheet && (
        <View style={styles.sheetOverlay}>
          <View style={styles.sheetContainer}>
            <Text style={styles.sheetTitle}>Escolha um modelo</Text>
            {loadingTemplates ? (
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
            ) : (
              templates.length === 0 ? (
                <Text style={styles.noDataText}>Nenhum modelo encontrado</Text>
              ) : (
                templates.map((tpl) => (
                  <TouchableOpacity
                    key={tpl.id}
                    style={styles.sheetItem}
                    onPress={() => handleSelectTemplate(tpl)}
                  >
                    <Text style={styles.sheetItemText}>{tpl.nome || 'Modelo sem nome'}</Text>
                  </TouchableOpacity>
                ))
              )
            )}
            <TouchableOpacity style={styles.sheetCancel} onPress={resetChecklistCreationFlow}>
              <Text style={styles.sheetCancelText}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Bottom Sheet/Modal de seleção do motorista do mesmo tenant */}
      {showDriverSheet && (
        <View style={styles.sheetOverlay}>
          <View style={[styles.sheetContainer, styles.driverSheetContainer]}>
            <Text style={styles.sheetTitle}>Escolha um motorista</Text>
            {loadingDrivers ? (
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
            ) : drivers.length === 0 ? (
              <Text style={styles.noDataText}>Nenhum motorista disponível nesta empresa.</Text>
            ) : (
              <ScrollView style={styles.driverList} contentContainerStyle={styles.driverListContent}>
                {drivers.map((driver) => {
                  const driverName = driver.name?.trim() || driver.email?.trim() || 'Motorista sem nome';
                  const showEmail = Boolean(driver.name?.trim() && driver.email?.trim());
                  return (
                    <TouchableOpacity
                      key={driver.id}
                      style={styles.sheetItem}
                      onPress={() => handleSelectDriver(driver)}
                      disabled={creatingChecklist}
                      accessibilityLabel={`Atribuir checklist para ${driverName}`}
                    >
                      <Text style={styles.sheetItemText}>{driverName}</Text>
                      {showEmail && <Text style={styles.sheetItemSecondaryText}>{driver.email}</Text>}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}
            <TouchableOpacity style={styles.sheetCancel} onPress={resetChecklistCreationFlow}>
              <Text style={styles.sheetCancelText}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
    paddingBottom: theme.spacing.md,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    paddingHorizontal: theme.spacing.lg,
  },
  loadingCard: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    ...theme.shadows.md,
  },
  loadingTitle: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    textAlign: 'center',
  },
  loadingText: {
    marginTop: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    lineHeight: 22,
  },
  loadingProgressTrack: {
    width: '100%',
    height: 10,
    marginTop: theme.spacing.lg,
    backgroundColor: theme.colors.background.tertiary,
    borderRadius: theme.borderRadius.circular,
    overflow: 'hidden',
  },
  loadingProgressFill: {
    height: '100%',
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.circular,
  },
  loadingProgressMeta: {
    marginTop: theme.spacing.sm,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    textAlign: 'center',
  },
  header: {
    backgroundColor: theme.colors.background.primary,
    paddingHorizontal: theme.spacing.md,
    //paddingVertical: theme.spacing.md,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.xs,
    marginBottom: theme.spacing.xs,
    ...theme.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  headerLeft: {
    flex: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  welcomeText: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  versionText: {
    marginTop: 4,
    fontSize: theme.fontSizes.xxs,
    color: theme.colors.text.tertiary,
  },
  statusBadge: {
    width: 72,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.borderRadius.circular,
  },
  statusText: {
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.inverse,
  },
  logoutChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.tertiary,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.circular,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  logoutIcon: {
    marginRight: theme.spacing.xs,
  },
  logoutChipText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  statusTextMultiline: {
    fontSize: 10,
    lineHeight: 10,
    textAlign: 'center',
  },
  syncBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EDF3FE',
    borderColor: '#DBE7FB',
    borderWidth: 1,
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    paddingLeft: 14,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.md,
  },
  syncBannerMessage: {
    flex: 1,
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
    color: '#28456F',
    paddingVertical: 8,
  },
  syncBannerClose: {
    width: 44,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  quickActions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  actionButton: {
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
  },
  actionButtonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
  },
  actionButtonText: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.inverse,
  },
  actionButtonTextSecondary: {
    color: theme.colors.primary.default,
  },
  card: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    ...theme.shadows.md,
  },
  cardTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md,
  },
  noDataText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    paddingVertical: theme.spacing.lg,
  },
  fabStack: {
    position: 'absolute',
    right: 24,
    bottom: 24,
    alignItems: 'center',
    gap: 14,
  },
  fabAction: {
    alignItems: 'center',
    gap: 8,
  },
  fab: {
    backgroundColor: theme.colors.primary.default,
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 6,
    zIndex: 10,
  },
  fabAssign: {
    backgroundColor: theme.colors.primary.dark,
  },
  fabLabelContainer: {
    backgroundColor: theme.colors.background.primary,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.pill,
    ...theme.shadows.sm,
  },
  fabLabelText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.medium as any,
  },
  sheetOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'flex-end',
    zIndex: 20,
  },
  sheetContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 24,
    minHeight: 180,
    maxHeight: '82%',
  },
  driverSheetContainer: {
    minHeight: 240,
  },
  sheetTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    color: theme.colors.text.primary,
  },
  sheetItem: {
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  sheetItemText: {
    fontSize: 16,
    color: theme.colors.text.primary,
  },
  sheetItemSecondaryText: {
    marginTop: 3,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  driverList: {
    flexGrow: 0,
    maxHeight: 360,
  },
  driverListContent: {
    paddingBottom: theme.spacing.xs,
  },
  sheetCancel: {
    marginTop: 16,
    alignItems: 'center',
  },
  sheetCancelText: {
    color: theme.colors.primary.default,
    fontSize: 16,
    fontWeight: 'bold',
  },
  onboardingOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: theme.spacing.lg,
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    zIndex: 30,
    pointerEvents: 'box-none' as const,
  },
  onboardingOverlaySecondary: {
    top: theme.spacing.lg * 4,
  },
  onboardingCardWrapper: {
    width: '100%',
    maxWidth: 420,
  },
  onboardingOverlayTertiary: {
    top: theme.spacing.lg * 2,
  },
});

export default HomeScreen; 
